import {
  Component,
  input,
  signal,
  AfterViewInit,
  OnDestroy,
  effect,
  ElementRef,
  output,

} from '@angular/core';
import { ZnPill } from '../zn-pill/zn-pill';
import { CommonModule } from '@angular/common';
import { IStepItem } from './interfaces/IStepItem';
import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';

@Component({
  selector: 'zn-stepper',
  standalone: true,
  imports: [CommonModule, ZnPill, ZendraLocalizationPipe],
  templateUrl: './zn-stepper.html',
  styleUrls: ['./zn-stepper.scss'],
})
export class ZnStepper implements AfterViewInit, OnDestroy {
  private readonly handleWindowResize = () => this.updateProgress();
  private resizeObserver?: ResizeObserver;

  constructor(private readonly el: ElementRef) { }

  steps = input<IStepItem[]>([]);

  activeIndex = input<number>(0);
  activeChange = output<number>();
  collapsed = signal(false);
  progressPx = signal(0);
  lineHeightPx = signal(0);

  progressEffect = effect(() => {
    this.activeIndex();

    requestAnimationFrame(() => {
      this.updateProgress();
    });
  });

  ngAfterViewInit() {
    requestAnimationFrame(() => this.updateProgress());

    window.addEventListener('resize', this.handleWindowResize);

    const stepsContainer = this.el.nativeElement.querySelector('.steps') as HTMLElement | null;

    if (stepsContainer && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateProgress());
      this.resizeObserver.observe(stepsContainer);
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.handleWindowResize);
    this.resizeObserver?.disconnect();
  }

  updateProgress() {
    const steps = this.el.nativeElement.querySelectorAll('.step');

    if (!steps.length) return;

    const firstStep = steps[0] as HTMLElement;
    const lastStep = steps[steps.length - 1] as HTMLElement;
    const activeStep = steps[this.activeIndex()] as HTMLElement;

    const firstCircle = firstStep.querySelector('.circle') as HTMLElement;
    const lastCircle = lastStep.querySelector('.circle') as HTMLElement;
    const activeCircle = activeStep.querySelector('.circle') as HTMLElement;

    if (!firstCircle || !lastCircle || !activeCircle) return;

    const containerRect = this.el.nativeElement
      .querySelector('.steps')
      .getBoundingClientRect();

    const lastRect = lastCircle.getBoundingClientRect();
    const totalHeight = this.getCircleCenter(containerRect, lastRect);

    this.lineHeightPx.set(totalHeight);

    if (this.activeIndex() === 0) {
      this.progressPx.set(0);
      return;
    }

    const activeRect = activeCircle.getBoundingClientRect();
    const progress = this.getCircleCenter(containerRect, activeRect);

    this.progressPx.set(progress);
  }

  private getCircleCenter(containerRect: DOMRect, circleRect: DOMRect) {
    return Math.max(0, Math.floor(circleRect.top - containerRect.top + circleRect.height / 2));
  }

  toggle() {
    this.collapsed.update(v => !v);

    requestAnimationFrame(() => this.updateProgress());
  }

  setActive(index: number) {
    this.activeChange.emit(index);
  }

  getState(index: number) {
    if (index < this.activeIndex()) return 'complete';
    if (index === this.activeIndex()) return 'active';
    return 'pending';
  }
}
